import { useAtom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import { useCallback, useMemo } from "react";
import { useSnapshot } from "valtio";
import { z } from "zod";

import { filterNulls } from "@blis/shared/utils/filterNulls";
import { trpc } from "@blis/trpc/react";

import type { TView } from "@/lib/types";
import {
  lruCache,
  persistedProxy,
  useLocalStorageQueue,
} from "@/utils/persistedState";
import type { TestOrderTreeGroupBy } from "../components/detail/TestOrderTree";

const emailHistoryAtom = atomWithStorage(
  "blis.email.history",
  [] as string[],
  undefined, // default localStorage w/JSON parsing
  { getOnInit: true },
);

export function useEmailHistory() {
  return useLocalStorageQueue(emailHistoryAtom);
}

const recentlyViewedProxy = persistedProxy(
  { recents: [] as number[] },
  "blis.accession.recent",
  z.object({
    recents: z.array(z.number()),
  }),
);

export function useRecentlyViewed() {
  const viewed = useSnapshot(recentlyViewedProxy).recents;
  const { push } = useSetRecentlyViewed();
  const accessions = trpc.accession.list.all.useQuery(
    {
      filter: { accessionIds: viewed as number[] },
    },
    {
      trpc: {
        context: {
          skipBatch: true,
        },
      },
    },
  );

  // this keeps order the same as viewed
  const fixedOrder = useMemo(
    () =>
      filterNulls(
        viewed.map((id) => accessions.data?.find((a) => a.id === id) ?? null),
      ),
    [accessions.data, viewed],
  );

  return [fixedOrder, push] as const;
}

export function useSetRecentlyViewed() {
  const push = useCallback((id: number) => {
    recentlyViewedProxy.recents = lruCache(recentlyViewedProxy.recents, id, {
      maxItems: 10,
    });
  }, []);
  return { push };
}

export function deletePersistedFilters(view: TView) {
  localStorage.removeItem(`blis.accession.list.filters.${view.id}`);
}

export function deletePersistedDisplay(view: TView) {
  localStorage.removeItem(`blis.accession.list.display.${view.id}`);
}

const testOrderGroupByAtom = atomWithStorage<TestOrderTreeGroupBy>(
  "blis.accession.testOrderGroupBy",
  "none",
  undefined, // default localStorage w/JSON parsing
  { getOnInit: true },
);

export function useTestOrderGroupBy() {
  return useAtom(testOrderGroupByAtom);
}

const timeZoneDisplayAtom = atomWithStorage<"local" | "collection">(
  "blis.accession.timeZoneDisplay",
  "local",
  undefined,
  { getOnInit: true },
);

export function useTimeZoneDisplay() {
  return useAtom(timeZoneDisplayAtom);
}
